import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type DocumentNode, NetworkStatus, type WatchQueryFetchPolicy, useQuery } from "@apollo/client";

import { RETRY_TRANSIENT } from "../../apollo/provider/createTransientRetryLink";
import {
    type PayoutPeriod,
    type PayoutProgram,
    type PayoutStatsInput,
    type PayoutStatsResponse,
    type PayoutTimeGranularity,
} from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { getPayoutStatsHistory } from "../api/queries/getPayoutStatsHistory";

interface UsePayoutStatsHistoryParams {
    payoutProgram?: PayoutProgram;
    startDate?: string;
    endDate?: string;
    countryFilters: string[];
    granularity: PayoutTimeGranularity;
    limit: number;
    unlockableId?: string;
    skip?: boolean;
    // A payoutStats document selecting a different field set; defaults to getPayoutStatsHistory.
    query?: DocumentNode;
    fetchPolicy?: WatchQueryFetchPolicy;
}

type UsePayoutStatsHistoryResult = ApolloResult<{
    periods: PayoutPeriod[];
    hasMore: boolean;
    fetchMore: () => void;
    loadingMore?: boolean;
    hasMoreError?: boolean;
    refetch?: () => void;
}>;

export const usePayoutStatsHistory = ({
    payoutProgram,
    startDate,
    endDate,
    countryFilters,
    granularity,
    limit,
    unlockableId,
    skip,
    query = getPayoutStatsHistory,
    fetchPolicy,
}: UsePayoutStatsHistoryParams): UsePayoutStatsHistoryResult => {
    const input: PayoutStatsInput = {
        payoutProgram,
        startDate,
        endDate,
        countryFilters,
        unlockableId,
        history: {
            granularity,
            limit,
        },
    };

    const requestKey = JSON.stringify(input);
    // Returning to identical variables still starts a new pagination snapshot.
    const scope = useMemo(() => ({ requestKey, query, skip, generation: 0 }), [requestKey, query, skip]);
    const currentScope = useRef(scope);
    currentScope.current = scope;
    useEffect(
        () => () => {
            scope.generation += 1;
        },
        [scope],
    );
    const fetchingMoreRef = useRef<{ scope: typeof scope; generation: number } | null>(null);
    const [pageError, setPageError] = useState<typeof fetchingMoreRef.current>(null);

    const {
        data,
        loading,
        error,
        networkStatus,
        refetch: apolloRefetch,
        fetchMore: apolloFetchMore,
    } = useQuery<{ payoutStats: PayoutStatsResponse }>(query, {
        variables: { input },
        fetchPolicy,
        skip,
        notifyOnNetworkStatusChange: true,
        context: RETRY_TRANSIENT,
    });

    const history = data?.payoutStats.history;
    const cursor = history?.cursor;
    const hasMore = Boolean(cursor);

    const periods = useMemo(() => [...(history?.periods ?? [])], [history?.periods]);

    const fetchMore = useCallback(() => {
        if (
            Boolean(skip) ||
            loading ||
            !cursor ||
            (fetchingMoreRef.current?.scope === scope && fetchingMoreRef.current.generation === scope.generation)
        )
            return;
        const request = { scope, generation: scope.generation };
        fetchingMoreRef.current = request;
        setPageError(null);

        void apolloFetchMore({
            variables: {
                input: {
                    payoutProgram,
                    startDate,
                    endDate,
                    countryFilters,
                    unlockableId,
                    history: {
                        granularity,
                        limit,
                        cursor,
                    },
                },
            },
            updateQuery: (previous, { fetchMoreResult }) => {
                if (currentScope.current !== scope || scope.generation !== request.generation) return previous;
                const nextHistory = fetchMoreResult.payoutStats.history;
                if (!nextHistory) return previous;
                return {
                    ...fetchMoreResult,
                    payoutStats: {
                        ...fetchMoreResult.payoutStats,
                        history: {
                            ...nextHistory,
                            periods: [...(previous.payoutStats.history?.periods ?? []), ...nextHistory.periods],
                        },
                    },
                };
            },
        })
            .catch(() => {
                if (currentScope.current === scope && scope.generation === request.generation) setPageError(request);
            })
            .finally(() => {
                if (fetchingMoreRef.current === request) fetchingMoreRef.current = null;
            });
    }, [
        apolloFetchMore,
        cursor,
        payoutProgram,
        startDate,
        endDate,
        countryFilters,
        unlockableId,
        granularity,
        limit,
        scope,
        skip,
        loading,
    ]);

    return {
        loading,
        error,
        periods,
        hasMore,
        fetchMore,
        loadingMore: networkStatus === NetworkStatus.fetchMore,
        hasMoreError: pageError?.scope === scope && pageError.generation === scope.generation,
        refetch: () => {
            scope.generation += 1;
            setPageError(null);
            void apolloRefetch().catch(() => {
                // Apollo exposes the failure through query state for the scoped retry UI.
            });
        },
    };
};
